import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { PATHWAYS, PATHWAY_ORDER } from "@/data/pathways";

const LINKS = [
  { to: "/", label: "Home", short: "Home" },
  ...PATHWAY_ORDER.map((k) => ({ to: PATHWAYS[k].route, label: PATHWAYS[k].menuLabel, short: PATHWAYS[k].shortLabel })),
];

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2 min-[380px]:gap-3 lg:gap-5 xl:gap-7 min-w-0 shrink-0" data-testid="nav-logo" aria-label="Legacy Wave Consulting — Home">
      <picture className="shrink-0">
        <source srcSet="/brand/emblem.webp" type="image/webp" />
        <img
          src="/brand/emblem.png"
          alt=""
          width={640}
          height={622}
          fetchPriority="high"
          className="h-[58px] w-auto min-[380px]:h-[66px] lg:h-[90px] xl:h-[110px] drop-shadow-[0_6px_14px_rgba(214,187,124,0.28)]"
          data-testid="brand-emblem"
        />
      </picture>
      <span className="leading-none whitespace-nowrap min-w-0">
        <span className="block lw-wordmark text-[20px] min-[380px]:text-[23px] lg:text-[31px] xl:text-[40px] tracking-[0.03em]">
          <span className="champagne-text">Legacy</span> <span className="text-[#F6F1E7]">Wave</span>
        </span>
        <span className="block mt-[4px] lg:mt-[6px] xl:mt-[8px] font-sans text-[8.5px] min-[380px]:text-[9.5px] lg:text-[12px] xl:text-[14px] tracking-[0.4em] lg:tracking-[0.5em] uppercase font-semibold text-[#E6CF97] pl-[3px]">Consulting</span>
        <span className="block mt-[4px] lg:mt-[6px] xl:mt-[8px] font-sans text-[7px] min-[380px]:text-[8px] lg:text-[9.5px] xl:text-[11px] tracking-[0.14em] min-[380px]:tracking-[0.18em] lg:tracking-[0.26em] xl:tracking-[0.3em] uppercase font-medium text-[#F6F1E7]/75 pl-[3px]" data-testid="brand-tagline">Discover Today. Protect Tomorrow.</span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className="sticky top-0 z-40 lw-header"
      style={{ backgroundImage: "linear-gradient(180deg, rgba(10,21,38,0.55) 0%, rgba(7,14,26,0.7) 60%, rgba(4,8,16,0.92) 100%), url(/brand/header-silk.webp)" }}
      data-testid="site-header"
    >
      <div className="relative max-w-7xl mx-auto px-3 min-[380px]:px-4 sm:px-8 lg:px-10 h-[92px] lg:h-[114px] xl:h-[136px] flex items-center justify-between gap-4">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-menu-button"
          className="lg:hidden w-10 min-[380px]:w-11 h-11 shrink-0 rounded-md text-[#D6BB7C] flex items-center justify-center hover:bg-[#D6BB7C]/10 transition-colors"
        >
          {open ? <X className="w-7 h-7" strokeWidth={1.7} /> : <Menu className="w-7 h-7" strokeWidth={1.7} />}
        </button>

        <div className="flex-1 flex justify-center lg:justify-start min-w-0">
          <Brand />
        </div>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-10 pl-6 xl:pl-10 border-l border-[#D6BB7C]/40 h-16 shrink-0" aria-label="Main" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end
              data-testid={`nav-link-${l.short.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] xl:text-[13px] font-semibold uppercase tracking-[0.22em] xl:tracking-[0.24em] transition-colors pb-1.5 border-b whitespace-nowrap ${
                  isActive ? "text-[#EBD6A3] border-[#D6BB7C]" : "text-[#F6F1E7]/85 border-transparent hover:text-[#EBD6A3]"
                }`
              }
            >
              {l.short}
            </NavLink>
          ))}
        </nav>

        <span className="hidden min-[420px]:block lg:hidden w-11 h-11 shrink-0" aria-hidden="true" />
      </div>
      <div className="lw-header-rule" />

      {open && (
        <nav id="mobile-menu" className="lg:hidden absolute inset-x-0 top-full bg-[#080F1C] border-b border-[#D6BB7C]/30 shadow-2xl" aria-label="Mobile" data-testid="nav-mobile-menu">
          <ul className="max-w-6xl mx-auto px-5 py-3 divide-y divide-white/5">
            {LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end
                  data-testid={`nav-mobile-link-${l.short.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between min-h-[52px] text-[15px] font-semibold ${isActive ? "text-[#EBD6A3]" : "text-[#F6F1E7]"}`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
